import React from 'react'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import KeyboardAltOutlinedIcon from '@mui/icons-material/KeyboardAltOutlined'

import ProfileEditorChart from '../charts/profileEditorChart'
import { GraphDatapoint, GraphSeriesEvents } from '../../types/graphSeriesConfig'
import {
  canDeletePoint,
  getSeriesLabel,
  getMainProfileParam,
  isSharedProfile,
  isPublicShareEnabled,
  getProfileSeries,
  getXaxisLimits,
  isBtBasedProfile,
  movePointsLeft,
  movePointsRight,
  movePointsUp,
  movePointsDown,
} from '../../utils/profileEditorUtils'
import { getGraphLimits, getValueSuffix } from '../../utils/graphUtils'
import { createInitialState, GraphPointReducerAction, graphPointsReducer } from '../../utils/graphPointsReducer'
import { Profile } from '../../types/profile'
import { EndCondition } from '../../types/enums/endCondition'
import { BasicDataParam, GraphDataParam } from '../../types/enums/graphDataParam'
import ConfirmationDialog from '../dialog/confirmationDialog'
import { Machine } from '../../types/machine'
import ProfileEditorForm from './profileEditorForm'
import KeyboardShortcutsModal from './keyboardShortcutsModal'
import { User } from '../../types/user'
import ProfileHistory from './profileHistory'
import ViewStepButtons from '../fields/viewStepButtons'
import TooltipIconButton from '../fields/tooltipIconButton'
import { SeriesType } from '../../types/enums/seriesType'
import ProfileMetadataEditor from '../profileLibrary/profileMetadataEditor'
import { ProfileEditorSeries } from '../../types/profileEditorSeries'
import ProfileEditorPoints from './profileEditorPoints'
import { ViewStep } from '../../types/enums/viewStep'
import { LEFT_COLUMN_WIDTH, RIGHT_COLUMN_WIDTH } from '../../utils/theme'
import ProfileRating from './profileRating'
import CompareSeriesDialog from './compareSeriesDialog'

const EDITOR_MAX_LENGTH = 1080000 // 18 minutes

interface ProfileEditorProps {
  isSharePreview: boolean
  profileData: Profile
  customerUrl: string
  customerId: number
  user: User | null
  machine: Machine | null
  logSeries?: ProfileEditorSeries
  saveProfile: (profile: Profile, isNewProfile: boolean) => void
  deleteProfile: (profile: Profile) => void
  addProfileToLibrary: (profile: Profile) => void
  onShareProfile: (profile: Profile) => void
  hasChanges: (profile: Profile) => boolean
}

/*
 * The profile editor should not do any calculations between Celsius and Fahrenheit. The temperature values
 * in props.profileData are already in the scale according to localStorageUtils.isFahrenheit. When saving,
 * createProfile.tsx will convert values to Celsius before saving.
 */
const ProfileEditor = (props: ProfileEditorProps): JSX.Element => {
  const { t } = useTranslation()
  const theme = useTheme()

  const [currentStep, setCurrentStep] = React.useState<ViewStep>(ViewStep.Left)
  const [profileName, setProfileName] = React.useState<string>(props.profileData.name)
  const [preheatTemp, setPreheatTemp] = React.useState<number>(props.profileData.preheat_temperature)
  const [batchWeight, setBatchWeight] = React.useState<number>(props.profileData.batch_weight)
  const [endCondition, setEndCondition] = React.useState<EndCondition>(
    props.profileData.end_condition || EndCondition.None
  )
  const [endConditionValue, setEndConditionValue] = React.useState<number>(props.profileData.end_condition_value)

  const [editParam, setEditParam] = React.useState<GraphDataParam>(getMainProfileParam(props.profileData.profile_type))
  const [selectedPointIndexes, setSelectedPointIndexes] = React.useState<Array<number>>([])

  const [minTime, maxTime] = getXaxisLimits(props.profileData.profile_type, EDITOR_MAX_LENGTH)
  const moveIntervalX = isBtBasedProfile(props.profileData.profile_type) ? 1 : 1000

  const [graphPointsState, graphPointsDispatch] = React.useReducer(
    graphPointsReducer,
    createInitialState(props.profileData, minTime)
  )

  const [isDeleteProfileModalOpen, setIsDeleteProfileModalOpen] = React.useState<boolean>(false)
  const [isKeyboardShortcutModalOpen, setIsKeyboardShortcutModalOpen] = React.useState<boolean>(false)
  const [notes, setNotes] = React.useState<string>(props.profileData.notes || '')
  const [selectedFilters, setSelectedFilters] = React.useState<Array<{ id: number; label: string }>>(
    props.profileData.selected_filters || []
  )
  const [imageData, setImageData] = React.useState<{ id?: number; b64_img_data: string; b64_img_type: string } | null>(
    props.profileData.metadata_image || null
  )
  const [isCompareDialogOpen, setIsCompareDialogOpen] = React.useState<boolean>(false)
  const [compareSeries, setCompareSeries] = React.useState<Array<ProfileEditorSeries> | null>(null)
  const [compareLogEvents, setCompareLogEvents] = React.useState<GraphSeriesEvents | null>(null)

  const isScreenBelowMdBreakpoint = useMediaQuery(theme.breakpoints.down('md'))

  const getSeriesConfig = React.useCallback((): Array<ProfileEditorSeries> => {
    return getProfileSeries({
      temperature_bezier: convertPointsBeforeSave(graphPointsState.tempPoints),
      power_bezier: convertPointsBeforeSave(graphPointsState.powerPoints),
      fan_bezier: convertPointsBeforeSave(graphPointsState.fanPoints),
      rpm_bezier: convertPointsBeforeSave(graphPointsState.rpmPoints),
      profile_type: props.profileData.profile_type,
      is_bbp_profile: props.profileData.is_bbp_profile,
    } as Profile)
  }, [
    graphPointsState.tempPoints,
    graphPointsState.fanPoints,
    graphPointsState.rpmPoints,
    graphPointsState.powerPoints,
    props.profileData.profile_type,
    props.profileData.is_bbp_profile,
  ])

  const isReadonlyEditor = React.useCallback((): boolean => {
    return props.isSharePreview
  }, [props.isSharePreview])

  const getCurrentPointsValueSuffix = React.useCallback((): string => {
    return getValueSuffix(getSeriesConfig().find((x) => x.param === editParam)?.seriesType || SeriesType.TEMPERATURE)
  }, [editParam, getSeriesConfig])

  const getCurrentPoints = React.useCallback((): Array<GraphDatapoint> => {
    return getSeriesConfig().find((x) => x.param === editParam)?.data || []
  }, [editParam, getSeriesConfig])

  const updatePoint = React.useCallback(
    (param: GraphDataParam, index: number, newTime: number, newValue: number): void => {
      // time cannot be negative (can happen when dragging point in highchart)
      const timeToSet = isBtBasedProfile(props.profileData.profile_type) ? newTime : Math.floor(newTime / 1000) * 1000
      graphPointsDispatch({
        type: GraphPointReducerAction.Update,
        param: param,
        index: index,
        newTime: timeToSet < minTime ? minTime : timeToSet,
        newValue: newValue,
      })
    },
    [props.profileData.profile_type, minTime]
  )

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isInputElementActive =
        document.activeElement &&
        ['input', 'textarea', 'select'].indexOf(document.activeElement.tagName.toLowerCase()) !== -1

      if (isInputElementActive || isDeleteProfileModalOpen || isReadonlyEditor()) {
        return false
      }

      const isMetaKeyPressed = event.metaKey || event.ctrlKey
      // undo: Ctrl/Cmd + Z
      if (event.code === 'KeyZ' && isMetaKeyPressed && !event.shiftKey && graphPointsState.historyIndex > 0) {
        graphPointsDispatch({ type: GraphPointReducerAction.Undo })
      }

      // redo: Ctrl/Cmd + Z + Shift OR Ctrl/Cmd + Y
      if (
        ((event.code === 'KeyZ' && isMetaKeyPressed && event.shiftKey) ||
          (event.code === 'KeyY' && isMetaKeyPressed)) &&
        graphPointsState.history.length > graphPointsState.historyIndex
      ) {
        graphPointsDispatch({ type: GraphPointReducerAction.Redo })
      }

      const currentPoints = selectedPointIndexes.map((index) => getCurrentPoints()[index])

      if (currentPoints.length > 0 && event.code === 'Delete') {
        const deleteArrayIndexes = selectedPointIndexes.filter((x) =>
          canDeletePoint(editParam, x, getCurrentPoints().length)
        )
        if (deleteArrayIndexes.length > 0) {
          setSelectedPointIndexes([])
          graphPointsDispatch({
            type: GraphPointReducerAction.BatchUpdate,
            param: editParam,
            batchUpdateArray: getCurrentPoints().filter((_x, index) => deleteArrayIndexes.indexOf(index) === -1),
          })
        }
      } else if (currentPoints.length > 0 && event.code === 'ArrowLeft') {
        const updatedValues = movePointsLeft(
          getCurrentPoints(),
          selectedPointIndexes,
          moveIntervalX,
          (startValue: number) => startValue - (isMetaKeyPressed ? moveIntervalX * 10 : moveIntervalX)
        )
        graphPointsDispatch({
          type: GraphPointReducerAction.BatchUpdate,
          param: editParam,
          batchUpdateArray: updatedValues,
        })
      } else if (currentPoints.length > 0 && event.code === 'ArrowRight') {
        const updatedValues = movePointsRight(
          getCurrentPoints(),
          selectedPointIndexes,
          moveIntervalX,
          maxTime,
          (startValue: number) => startValue + (isMetaKeyPressed ? moveIntervalX * 10 : moveIntervalX)
        )
        graphPointsDispatch({
          type: GraphPointReducerAction.BatchUpdate,
          param: editParam,
          batchUpdateArray: updatedValues,
        })
      } else if (currentPoints.length > 0 && event.code === 'ArrowUp') {
        const updatedValues = movePointsUp(
          getCurrentPoints(),
          selectedPointIndexes,
          getGraphLimits(editParam).max,
          (startValue: number) => startValue + (isMetaKeyPressed ? 10 : 1)
        )
        graphPointsDispatch({
          type: GraphPointReducerAction.BatchUpdate,
          param: editParam,
          batchUpdateArray: updatedValues,
        })
      } else if (currentPoints.length > 0 && event.code === 'ArrowDown') {
        const updatedValues = movePointsDown(
          getCurrentPoints(),
          selectedPointIndexes,
          getGraphLimits(editParam).min,
          (startValue: number) => startValue - (isMetaKeyPressed ? 10 : 1)
        )
        graphPointsDispatch({
          type: GraphPointReducerAction.BatchUpdate,
          param: editParam,
          batchUpdateArray: updatedValues,
        })
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    selectedPointIndexes,
    editParam,
    maxTime,
    moveIntervalX,
    isDeleteProfileModalOpen,
    graphPointsState.history.length,
    graphPointsState.historyIndex,
    updatePoint,
    getCurrentPoints,
    isReadonlyEditor,
  ])

  React.useEffect(() => {
    // set image again after save, to set correct id generated by server
    if (props.profileData.metadata_image?.id) {
      setImageData(props.profileData.metadata_image)
    }
  }, [props.profileData.metadata_image])

  const createNewPoint = (param: GraphDataParam, newTime: number, newValue: number): void => {
    let time = newTime
    if (newTime < minTime) {
      time = minTime
    } else if (newTime > maxTime) {
      time = maxTime
    }

    let value = newValue
    const minValue = getGraphLimits(param).min
    const maxValue = getGraphLimits(param).max
    if (newValue < minValue) {
      value = minValue
    } else if (newValue > maxValue) {
      value = maxValue
    }

    graphPointsDispatch({
      type: GraphPointReducerAction.Add,
      param: param,
      newTime: time,
      newValue: value,
    })
  }

  const generateShareButton = () => {
    let buttonText = t('profilesview.enableshare')
    let isShared = false
    // public share
    if (isPublicShareEnabled(props.profileData)) {
      isShared = true
      buttonText = t('profilesview.changeshareoptions')
    } else if (props.profileData.share_uuid) {
      isShared = true
      buttonText = t('profilesview.changeshareoptions')
    }
    return (
      <Button
        variant={isShared ? 'text' : 'outlined'}
        onClick={(): void => {
          const saveData = getProfileDataToSave()
          props.onShareProfile(saveData)
        }}
      >
        {buttonText}
      </Button>
    )
  }

  const canRateProfile = (): boolean => {
    return isPublicShareEnabled(props.profileData) && props.customerUrl !== props.profileData.customer
  }

  const convertPointsBeforeSave = (points: Array<GraphDatapoint>): number[][] => {
    return points.length < 2 ? [] : points.map((point) => [point.msec, point.dataValue || 0])
  }

  const getProfileDataToSave = (): Profile => {
    const seriesConfig = getSeriesConfig()
    const powerSeriesData = seriesConfig.find((x) => x.param === BasicDataParam.POWER)
    const fanSeriesData = seriesConfig.find((x) => x.param === BasicDataParam.FAN)
    const rpmSeriesData = seriesConfig.find((x) => x.param === BasicDataParam.RPM)
    const tempSeriesData = seriesConfig.find(
      (x) => x.param === BasicDataParam.BT || x.param === BasicDataParam.ET || x.param === BasicDataParam.INLET
    )
    return {
      ...props.profileData,
      name: profileName,
      profile_type: props.profileData.profile_type,
      temperature_bezier: tempSeriesData ? convertPointsBeforeSave(tempSeriesData.data) : null,
      power_bezier: powerSeriesData ? convertPointsBeforeSave(powerSeriesData.data) : null,
      rpm_bezier: convertPointsBeforeSave(rpmSeriesData?.data || []),
      fan_bezier: convertPointsBeforeSave(fanSeriesData?.data || []),
      runlength: 0, // TODO: remove
      customer: props.customerUrl,
      batch_weight: batchWeight,
      preheat_temperature: preheatTemp,
      end_condition: endCondition,
      end_condition_value: endConditionValue,
      notes: notes || null,
      selected_filters: selectedFilters,
      metadata_image: imageData,
    }
  }

  const saveProfile = (isNewProfile: boolean): void => {
    const saveData = getProfileDataToSave()
    props.saveProfile(saveData, isNewProfile)
  }

  const changeSelectedSeries = (currentSeries: GraphDataParam) => {
    setEditParam(currentSeries)
    if (currentSeries !== editParam) {
      setSelectedPointIndexes([])
    }
  }

  const shouldShowStep = (step: ViewStep) => {
    return !isScreenBelowMdBreakpoint || (isScreenBelowMdBreakpoint && currentStep === step)
  }

  const generateUndoRedoElement = (): JSX.Element => {
    return (
      <Stack direction="row">
        <Button
          disabled={graphPointsState.historyIndex === 0}
          onClick={() => graphPointsDispatch({ type: GraphPointReducerAction.Undo })}
        >
          {t('editprofileview.undo')}
        </Button>
        <Button
          disabled={graphPointsState.history.length === graphPointsState.historyIndex}
          onClick={() => graphPointsDispatch({ type: GraphPointReducerAction.Redo })}
        >
          {t('editprofileview.redo')}
        </Button>
      </Stack>
    )
  }

  const generateSaveButtons = (isMobileButtons?: boolean): JSX.Element => {
    return (
      <Stack direction={isMobileButtons ? 'row' : 'column'} sx={{ justifyContent: 'flex-end', pb: 1 }} spacing={1}>
        {!isMobileButtons && <Divider />}
        {props.isSharePreview && (
          <Button
            size={isMobileButtons ? 'small' : undefined}
            variant="contained"
            onClick={() => props.addProfileToLibrary(props.profileData)}
          >
            {t('editprofileview.addtolibrary')}
          </Button>
        )}
        {!isReadonlyEditor() && !isSharedProfile(props.profileData) && props.profileData.is_leaf && (
          <Button
            size={isMobileButtons ? 'small' : undefined}
            variant="contained"
            disabled={!profileName.trim() || !props.hasChanges(getProfileDataToSave())}
            onClick={() => saveProfile(!props.profileData.url)}
          >
            {props.profileData.url ? t('editprofileview.updateprofile') : t('editprofileview.createprofile')}
          </Button>
        )}
        <Stack direction="row" spacing={1}>
          {!!props.profileData.url && !props.isSharePreview && (
            <Button
              sx={{ flex: 1 }}
              size={isMobileButtons ? 'small' : undefined}
              variant="outlined"
              disabled={!profileName.trim()}
              onClick={() => saveProfile(true)}
            >
              {t('editprofileview.saveasnewprofile')}
            </Button>
          )}
          {!!props.profileData.url && props.profileData.is_leaf && !isReadonlyEditor() && (
            <Button
              sx={{ flex: 1 }}
              size={isMobileButtons ? 'small' : undefined}
              variant="outlined"
              color="error"
              onClick={(): void => setIsDeleteProfileModalOpen(true)}
            >
              {props.customerUrl !== props.profileData.customer
                ? t('editprofileview.removeprofile')
                : t('editprofileview.deleteprofile')}
            </Button>
          )}
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack
      direction={isScreenBelowMdBreakpoint ? 'column' : 'row'}
      sx={{ flex: 1, overflow: 'hidden', justifyContent: 'space-between' }}
      spacing={2}
    >
      {isDeleteProfileModalOpen && props.profileData && (
        <ConfirmationDialog
          header={
            props.customerUrl === props.profileData.customer
              ? t('editprofileview.deleteprofileheader').replace('{0}', props.profileData.name)
              : t('editprofileview.removeprofileheader').replace('{0}', props.profileData.name)
          }
          body={
            props.customerUrl === props.profileData.customer
              ? t('editprofileview.deleteprofilebody')
              : t('editprofileview.removeprofilebody')
          }
          confirmButtonText={
            props.customerUrl === props.profileData.customer
              ? t('editprofileview.deleteprofile')
              : t('editprofileview.removeprofile')
          }
          type="error"
          onConfirm={(): void => {
            props.deleteProfile(props.profileData)
            setIsDeleteProfileModalOpen(false)
          }}
          onClose={(): void => setIsDeleteProfileModalOpen(false)}
        />
      )}
      {isCompareDialogOpen && props.machine && (
        <CompareSeriesDialog
          profileBase={props.profileData}
          onClose={() => setIsCompareDialogOpen(false)}
          onSelectPoints={(comparePoints: Array<ProfileEditorSeries>, logEvents: GraphSeriesEvents | null) => {
            setCompareSeries(comparePoints)
            setCompareLogEvents(logEvents)
            setIsCompareDialogOpen(false)
          }}
          currentMachineId={props.machine.id}
          customerId={props.customerId}
        />
      )}
      {isKeyboardShortcutModalOpen && <KeyboardShortcutsModal onClose={() => setIsKeyboardShortcutModalOpen(false)} />}
      {/* step 1 */}
      {shouldShowStep(ViewStep.Left) && (
        <Stack
          sx={{ overflowY: 'auto', overflowX: 'hidden', width: isScreenBelowMdBreakpoint ? '100%' : LEFT_COLUMN_WIDTH }}
        >
          <ProfileEditorForm
            isReadonly={isReadonlyEditor()}
            machine={props.machine}
            editorMaxLength={EDITOR_MAX_LENGTH}
            profileName={profileName}
            isBbpProfile={props.profileData.is_bbp_profile}
            setProfileName={setProfileName}
            isProfileTypeReadonly={!!props.profileData.url}
            profileType={props.profileData.profile_type}
            preheatTemp={preheatTemp}
            setPreheatTemp={setPreheatTemp}
            batchWeight={batchWeight}
            setBatchWeight={setBatchWeight}
            endCondition={endCondition}
            setEndCondition={(newEndCondition: EndCondition): void => {
              setEndCondition(newEndCondition)
              setEndConditionValue(0)
            }}
            endConditionValue={endConditionValue}
            setEndConditionValue={setEndConditionValue}
          >
            <Stack spacing={1}>
              <ProfileMetadataEditor
                isReadonly={isReadonlyEditor()}
                profile={getProfileDataToSave()}
                notes={notes}
                onNotesChanged={setNotes}
                selectedFilters={selectedFilters}
                onFiltersChanged={(value: number, isChecked: boolean) => {
                  setSelectedFilters((oldFilters) => {
                    return isChecked
                      ? [...oldFilters, { id: value, label: 'test' }]
                      : oldFilters.filter((y) => y.id !== value)
                  })
                }}
                imageData={imageData}
                onImageDataChanged={(b64Data: string, mimeType: string) => {
                  setImageData({ b64_img_data: b64Data, b64_img_type: mimeType })
                }}
              />
              {props.customerUrl === props.profileData.customer && props.profileData.parent ? (
                <ProfileHistory profileData={props.profileData} />
              ) : null}
              {props.profileData.url && props.customerUrl === props.profileData.customer && generateShareButton()}
              {!props.profileData.is_bbp_profile && (
                <Button onClick={() => setIsCompareDialogOpen(true)}>{t('editprofileview.compareprofile')}</Button>
              )}
              {canRateProfile() && <ProfileRating profile={props.profileData} />}
            </Stack>
          </ProfileEditorForm>
        </Stack>
      )}
      {/* step 2 */}
      {shouldShowStep(ViewStep.Center) && (
        <Stack sx={{ overflow: 'hidden', flex: 1, pt: 1, position: 'relative' }}>
          <ProfileEditorChart
            selectedSeries={editParam}
            isReadonlyProfile={isReadonlyEditor()}
            autoDropTime={
              endCondition === EndCondition.RoastingTimeTarget && !isBtBasedProfile(props.profileData.profile_type)
                ? endConditionValue
                : 0
            }
            profileType={props.profileData.profile_type}
            seriesConfig={getSeriesConfig()}
            logSeries={props.logSeries}
            compareSeries={compareSeries || []}
            compareLogEvents={compareLogEvents}
            setCurrentSeries={(currentSeries: GraphDataParam) => changeSelectedSeries(currentSeries)}
            onDropPoint={updatePoint}
            minX={minTime}
            maxX={maxTime}
            moveIntervalX={moveIntervalX}
            onCreatePoint={createNewPoint}
            selectedPointIndexes={selectedPointIndexes}
            selectPoint={(index: number, isAccumulate: boolean) =>
              setSelectedPointIndexes((oldIndexes) => {
                return isAccumulate ? [...oldIndexes, index] : [index]
              })
            }
            unselectPoint={(index: number, isAccumulate: boolean) =>
              setSelectedPointIndexes((oldIndexes) => {
                if (isAccumulate) {
                  return oldIndexes.filter((x) => x !== index)
                }
                return oldIndexes.indexOf(index) > -1 ? [] : oldIndexes
              })
            }
            user={props.user}
            keyboardShortcutButton={
              <TooltipIconButton
                onClick={() => setIsKeyboardShortcutModalOpen(true)}
                tooltip={t('editprofileview.showkeyboardshortcuts')}
                icon={<KeyboardAltOutlinedIcon />}
              />
            }
          />
          {isScreenBelowMdBreakpoint && generateUndoRedoElement()}
        </Stack>
      )}
      {/* step 3 */}
      {shouldShowStep(ViewStep.Right) && (
        <Stack
          spacing={1}
          sx={{ width: RIGHT_COLUMN_WIDTH, justifyContent: 'space-between', overflowY: 'auto', overflowX: 'hidden' }}
        >
          <Stack sx={{ pt: 1, overflowY: 'auto' }}>
            <FormControl>
              <InputLabel htmlFor="edit-param-select">{t('editprofileview.editparameter')}</InputLabel>
              <Select
                native
                id="edit-param-select"
                value={editParam}
                label={t('editprofileview.editparameter')}
                size="small"
                onChange={(event: SelectChangeEvent): void =>
                  changeSelectedSeries(event.target.value as GraphDataParam)
                }
              >
                {getSeriesConfig().map((x) => (
                  <option key={x.param} value={x.param}>
                    {t(getSeriesLabel(x.param))}
                  </option>
                ))}
              </Select>
            </FormControl>
            <ProfileEditorPoints
              editParam={editParam}
              points={getCurrentPoints()}
              selectedPointIndexes={selectedPointIndexes}
              valueSuffix={getCurrentPointsValueSuffix()}
              minX={minTime}
              maxX={maxTime}
              moveIntervalX={moveIntervalX}
              isReadonly={isReadonlyEditor()}
              isXaxisTemp={isBtBasedProfile(props.profileData.profile_type)}
              onDeletePoint={(param: GraphDataParam, index: number) => {
                setSelectedPointIndexes([])
                graphPointsDispatch({
                  type: GraphPointReducerAction.Delete,
                  param: param,
                  index: index,
                })
              }}
              onChangePoint={(param: GraphDataParam, index: number, newTime: number, newValue: number) => {
                updatePoint(param, index, newTime, newValue)
                setSelectedPointIndexes([])
              }}
              onCreateNewPoint={createNewPoint}
              onBatchUpdate={(param: GraphDataParam, pointsArray: Array<GraphDatapoint>) => {
                graphPointsDispatch({
                  type: GraphPointReducerAction.BatchUpdate,
                  param: param,
                  batchUpdateArray: pointsArray,
                })
              }}
            />
            {generateUndoRedoElement()}
          </Stack>
          {!isScreenBelowMdBreakpoint && generateSaveButtons()}
        </Stack>
      )}
      {isScreenBelowMdBreakpoint && (
        <ViewStepButtons
          steps={[ViewStep.Left, ViewStep.Center, ViewStep.Right]}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          children={generateSaveButtons(true)}
        />
      )}
    </Stack>
  )
}

export default ProfileEditor
